import { Controller } from "@hotwired/stimulus"

// Renders an SVG area+line chart of track width along lap distance.
// Width series = wL+wR per center_line point (pre-summed by the ERB template).
// Survives Turbo frame swaps: buildChart() is called fresh every connect().
export default class extends Controller {
  static values = {
    widths: Array, // JSON array of wL+wR per point (downsampled to ~250-300 pts)
    length: Number, // total lap length in meters
    sectors: Array, // fractions [0, s2_frac, s3_frac, 1]
  }

  connect() {
    this.buildChart()
    this._onMouseMove = this._handleMouseMove.bind(this)
    this._onMouseLeave = this._handleMouseLeave.bind(this)
    this._chartEl?.addEventListener("mousemove", this._onMouseMove)
    this._chartEl?.addEventListener("mouseleave", this._onMouseLeave)
  }

  disconnect() {
    this._chartEl?.removeEventListener("mousemove", this._onMouseMove)
    this._chartEl?.removeEventListener("mouseleave", this._onMouseLeave)
  }

  buildChart() {
    const wp = this.widthsValue
    if (!wp || wp.length < 2) return

    const style = getComputedStyle(this.element)
    const accentColor = style.getPropertyValue("--color-vivid-purple").trim()
    const faintColor = style.getPropertyValue("--color-app-text-faint").trim()
    const borderColor = style.getPropertyValue("--color-app-border").trim()

    const n = wp.length
    const total = this.lengthValue || n
    const W = 1000
    const H = 90
    const CH = 76 // rendered px height of the chart area

    const max = Math.max(...wp) * 1.1
    const min = Math.min(...wp) * 0.9
    const range = max - min || 1

    const x = (i) => (i / (n - 1)) * W
    const y = (v) => H - ((v - min) / range) * H

    // Build SVG path strings
    let area = `M0 ${H}`
    wp.forEach((v, i) => {
      area += `L${x(i).toFixed(1)} ${y(v).toFixed(1)}`
    })
    area += `L${W} ${H}Z`

    let line = ""
    wp.forEach((v, i) => {
      line += (i ? "L" : "M") + x(i).toFixed(1) + " " + y(v).toFixed(1)
    })

    // Sector dividers — use provided fractions (skip index 0 and 1 = endpoints)
    const sectors = this.sectorsValue || [0, 1 / 3, 2 / 3, 1]
    const dividerFracs = sectors.slice(1, -1) // interior boundaries only

    const dividers = dividerFracs
      .map((f) => {
        const xPos = (f * W).toFixed(1)
        return `<line x1="${xPos}" y1="0" x2="${xPos}" y2="${H}" stroke="${borderColor}" stroke-width="1.5" stroke-dasharray="4 4" />`
      })
      .join("")

    // Gradient id scoped to avoid clashes if multiple charts on the page
    const gradId = `wp-fill-${Math.random().toString(36).slice(2, 8)}`

    const svgMarkup = `
      <svg viewBox="0 0 ${W} ${H}" width="100%" height="${CH}"
           preserveAspectRatio="none" style="display:block">
        <defs>
          <linearGradient id="${gradId}" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stop-color="${accentColor}" stop-opacity="0.30"/>
            <stop offset="1" stop-color="${accentColor}" stop-opacity="0.03"/>
          </linearGradient>
        </defs>
        ${dividers}
        <path d="${area}" fill="url(#${gradId})"/>
        <path d="${line}" fill="none" stroke="${accentColor}" stroke-width="2" vector-effect="non-scaling-stroke"/>
      </svg>
    `

    // Axis ticks
    const axisTicks = [0, 0.25, 0.5, 0.75, 1]
    const ticksMarkup = axisTicks
      .map((f, i) => {
        const label =
          Math.round(f * total).toLocaleString() +
          (i === axisTicks.length - 1 ? " m" : "")
        const alignStyle =
          i === 0
            ? "transform:none"
            : i === axisTicks.length - 1
              ? "transform:translateX(-100%)"
              : "transform:translateX(-50%)"
        return `<span style="position:absolute;left:${f * 100}%;font-size:9.5px;color:${faintColor};${alignStyle};font-variant-numeric:tabular-nums">${label}</span>`
      })
      .join("")

    // Hover guide, dot, and popover (absolutely positioned over the chart)
    const hoverMarkup = `
      <div data-wp-guide style="display:none;position:absolute;top:0;bottom:0;width:1px;background:rgba(255,255,255,.22);pointer-events:none"></div>
      <div data-wp-dot style="display:none;position:absolute;width:8px;height:8px;border-radius:9999px;background:${accentColor};box-shadow:0 0 0 2px var(--color-app-surface-alt,#1e2135);transform:translate(-50%,-50%);pointer-events:none"></div>
      <div data-wp-popover style="display:none;position:absolute;pointer-events:none;background:var(--color-app-popover-bg,#232740);box-shadow:0 4px 16px rgba(0,0,0,.4),0 0 0 1px ${borderColor};border-radius:8px;padding:7px 10px;white-space:nowrap;z-index:5">
        <div data-wp-width style="font-family:var(--font-mono,monospace);font-size:14px;font-weight:700;color:var(--color-app-text,#fff);line-height:1.1"></div>
        <div data-wp-pos style="font-family:var(--font-mono,monospace);font-size:10px;color:${faintColor};margin-top:2px"></div>
      </div>
    `

    this.element.innerHTML = `
      <p class="font-mono text-[10.5px] font-bold tracking-[.06em] uppercase text-app-text-faint mb-2">
        WIDTH PROFILE — by lap distance
      </p>
      <div class="relative rounded-[10px] bg-app-surface-alt px-3 py-2.5">
        <div data-wp-chart style="position:relative;height:${CH}px;cursor:crosshair">
          ${svgMarkup}
          ${hoverMarkup}
        </div>
        <div style="position:relative;height:14px;margin-top:4px">
          ${ticksMarkup}
        </div>
      </div>
    `

    this._chartEl = this.element.querySelector("[data-wp-chart]")
    this._guide = this.element.querySelector("[data-wp-guide]")
    this._dot = this.element.querySelector("[data-wp-dot]")
    this._popover = this.element.querySelector("[data-wp-popover]")
    this._wLabel = this.element.querySelector("[data-wp-width]")
    this._pLabel = this.element.querySelector("[data-wp-pos]")

    // Re-bind events since innerHTML replaced elements
    this._chartEl.addEventListener("mousemove", this._onMouseMove)
    this._chartEl.addEventListener("mouseleave", this._onMouseLeave)

    // Store computed layout info for mousemove
    this._chartH = CH
    this._chartHsvg = H
    this._yFn = y
    this._wArr = wp
    this._nPts = n
    this._totalM = total
  }

  _handleMouseMove(e) {
    if (!this._chartEl) return
    const rect = this._chartEl.getBoundingClientRect()
    const frac = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width))
    const idx = Math.round(frac * (this._nPts - 1))
    const w = this._wArr[idx]
    const posM = Math.round(frac * this._totalM)

    const px = frac * rect.width
    // dot: vertical position uses the SVG y mapping scaled to rendered height
    const dotTop = (this._yFn(w) / this._chartHsvg) * this._chartH

    this._guide.style.left = `${px}px`
    this._guide.style.display = ""

    this._dot.style.left = `${px}px`
    this._dot.style.top = `${dotTop}px`
    this._dot.style.display = ""

    // Popover: clamp x so it stays within the chart, 46px buffer from edges
    const popoverX = Math.min(Math.max(px, 46), rect.width - 46)
    this._popover.style.left = `${popoverX}px`
    this._popover.style.top = "-14px"
    this._popover.style.transform = "translate(-50%, -100%)"
    this._popover.style.display = ""

    this._wLabel.innerHTML = `${w.toFixed(1)}<span style="font-size:10px;color:var(--color-app-text-faint,#6b7280);margin-left:2px">m wide</span>`
    this._pLabel.textContent = `pos ${posM.toLocaleString()} m`
  }

  _handleMouseLeave() {
    if (this._guide) this._guide.style.display = "none"
    if (this._dot) this._dot.style.display = "none"
    if (this._popover) this._popover.style.display = "none"
  }
}
